<script setup>
import { computed, ref } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { formatBytes, fileSizeInMegaBytes } from 'shared/helpers/FileHelper';

const props = defineProps({
  label: { type: String, default: 'Arquivo para o contato' },
  file: { type: Object, default: null },
  isUploading: { type: Boolean, default: false },
  maxSizeMb: { type: Number, default: 40 },
});

const emit = defineEmits(['select', 'remove', 'invalid']);

// Interface texts kept in script, following the funnel page pattern.
const copy = {
  uploading: ' · Enviando…',
  removeFile: 'Remover arquivo',
  maxSize: 'Tamanho máximo:',
  megabytes: 'MB',
};

const inputRef = ref(null);
const isDragging = ref(false);

const hasFile = computed(() => Boolean(props.file?.name));
const fileSizeLabel = computed(() =>
  Number(props.file?.size) > 0 ? formatBytes(Number(props.file.size), 1) : ''
);
const fileExtension = computed(() => {
  const name = String(props.file?.name || '');
  const index = name.lastIndexOf('.');
  return index > 0 ? name.slice(index + 1).toUpperCase() : '';
});

const openPicker = () => {
  if (props.isUploading) return;
  inputRef.value?.click();
};

const acceptFile = file => {
  if (!file || props.isUploading) return;
  if (fileSizeInMegaBytes(file.size) > props.maxSizeMb) {
    emit('invalid', `O arquivo excede o limite de ${props.maxSizeMb} MB.`);
    return;
  }
  if (!file.size) {
    emit('invalid', 'O arquivo selecionado está vazio.');
    return;
  }
  emit('select', file);
};

const onInputChange = event => {
  const [file] = Array.from(event.target.files || []);
  acceptFile(file);
  event.target.value = '';
};

const onDrop = event => {
  isDragging.value = false;
  const [file] = Array.from(event.dataTransfer?.files || []);
  acceptFile(file);
};

const onDragOver = () => {
  if (!props.isUploading) isDragging.value = true;
};
</script>

<template>
  <div class="grid gap-1" data-testid="node-file-field">
    <span class="mb-0.5 text-sm font-medium text-n-slate-12">{{ label }}</span>
    <input
      ref="inputRef"
      type="file"
      class="hidden"
      tabindex="-1"
      aria-hidden="true"
      data-testid="node-file-input"
      @change="onInputChange"
    />
    <div
      v-if="hasFile"
      class="flex flex-wrap items-center gap-3 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5"
      :aria-busy="isUploading"
    >
      <span
        class="grid size-10 shrink-0 place-content-center rounded-lg bg-n-alpha-2 text-n-slate-11"
        aria-hidden="true"
      >
        <span class="i-hugeicons-file-attachment size-5" />
      </span>
      <div class="min-w-0 flex-1">
        <p
          class="mb-0 truncate text-sm font-medium text-n-slate-12"
          :title="file.name"
        >
          {{ file.name }}
        </p>
        <p class="mb-0 text-xs text-n-slate-11">
          <span v-if="fileExtension">{{ fileExtension }}</span>
          <span v-if="fileExtension && fileSizeLabel"> · </span>
          <span v-if="fileSizeLabel">{{ fileSizeLabel }}</span>
          <span v-if="isUploading">{{ copy.uploading }}</span>
        </p>
      </div>
      <div class="flex shrink-0 items-center gap-1">
        <NextButton
          type="button"
          size="sm"
          variant="faded"
          color="slate"
          icon="i-hugeicons-refresh"
          label="Trocar"
          :is-loading="isUploading"
          :disabled="isUploading"
          @click="openPicker"
        />
        <NextButton
          type="button"
          size="sm"
          variant="ghost"
          color="ruby"
          icon="i-hugeicons-delete-02"
          :aria-label="copy.removeFile"
          :title="copy.removeFile"
          :disabled="isUploading"
          @click="emit('remove')"
        />
      </div>
    </div>
    <div
      v-else
      class="grid place-items-center gap-2 rounded-lg border border-dashed px-4 py-5 text-center transition-colors"
      :class="
        isDragging
          ? 'border-n-brand bg-n-brand/5'
          : 'border-n-weak bg-n-solid-1 hover:border-n-slate-7'
      "
      data-testid="node-file-dropzone"
      :aria-busy="isUploading"
      @dragenter.prevent="onDragOver"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="onDrop"
    >
      <span
        class="size-6 text-n-slate-10"
        :class="
          isUploading
            ? 'i-hugeicons-loading-03 motion-safe:animate-spin'
            : 'i-hugeicons-cloud-upload'
        "
        aria-hidden="true"
      />
      <p class="mb-0 text-sm text-n-slate-11">
        {{
          isUploading
            ? 'Enviando arquivo…'
            : 'Arraste um arquivo para cá ou selecione no computador.'
        }}
      </p>
      <NextButton
        type="button"
        size="sm"
        variant="faded"
        color="slate"
        icon="i-hugeicons-upload-04"
        label="Selecionar arquivo"
        :is-loading="isUploading"
        :disabled="isUploading"
        @click="openPicker"
      />
      <p class="mb-0 text-[11px] text-n-slate-10">
        {{ copy.maxSize }} {{ maxSizeMb }} {{ copy.megabytes }}
      </p>
    </div>
  </div>
</template>
